import "./styles/rare.css";
import { RARITY_IDS, Rarity } from "@highmarch/domain";
import type { BalanceData, GearItem } from "@highmarch/domain";
import { gearWellHtml } from "./hub/itemBlock";
import { fmtCompact } from "./kit";
import type { RareDef } from "../rare/rareApi";
import { bramStill } from "./guide/bram/bramView";
import type { BossBar } from "./bossBar";
import { motionOk } from "./kit";
import { portraitUrl, resourceIconUrl } from "./portraits";
import { RARITY_LABEL } from "./gearUi";

/**
 * The rare spawn's DOM (M36 §5): the announcement (banner, portrait, Captain Bram's line), the golden boss bar, the
 * mid-level chest reveal and the escape. Transform/opacity animations only, everything pointer-transparent except the
 * chest card (a tap sends it on its way early); nothing here pauses the sim. Captain Bram appears as his still render
 * (bramView.ts `bramStill`), never his live 3D stage: no second WebGL context mid-battle (M33).
 */

/** CSS colour of a 0xRRGGBB. */
const css = (hex: number): string => `#${hex.toString(16).padStart(6, "0")}`;

/** Captain Bram's lines for the rare moment, by beat. */
export const RARE_LINES = {
  spawn: (name: string) => `A rare ${name}, Commander! Drop it before the gate and its chest is ours.`,
  kill: () => "What a prize! That chest is ours, come what may.",
  escape: (name: string) => `The ${name} slipped through. We'll have the next one.`,
} as const;

/** How long the spawn announcement holds (ms), and when its title slams in (the stinger's hit lands at ~0.9 s). */
export const RARE_BANNER = { HoldMs: 3600, SlamMs: 880 } as const;

/** Sets the colours a rare's DOM pieces read (--rare-a / --rare-b) on `el`. */
function paint(el: HTMLElement, def: RareDef): void {
  el.style.setProperty("--rare-a", css(def.sparkA));
  el.style.setProperty("--rare-b", css(def.sparkB));
}

/**
 * "A RARE GILDED STAG APPEARS!" over the map: a burst of rays behind the creature's portrait in a gold ring, the
 * title on a gold ribbon, and Captain Bram's still with his line underneath. The stinger's swell plays under the first
 * `SlamMs`; the title slams in on its hit.
 */
export class RareBanner {
  private el: HTMLDivElement | null = null;
  private timer = 0;

  constructor(private readonly parent: HTMLElement) {}

  show(def: RareDef): void {
    this.hide();
    const el = document.createElement("div");
    el.className = "rare-banner";
    paint(el, def);
    const pic = portraitUrl(def.id);
    el.innerHTML = `<div class="rb-rays"></div><div class="rb-face">${pic ? `<img decoding="sync" src="${pic}" alt="">` : ""}<i class="rb-star"></i></div>`
      + `<div class="ribbon disp rb-title">A RARE ${def.name.toUpperCase()} APPEARS!</div>`
      + `<div class="plate rb-sub">Slay it for a <b>rare chest</b> · it must not reach the gate</div>`
      + `<div class="rb-bram"><div class="rb-bust"></div><div class="rb-say">${RARE_LINES.spawn(def.name)}</div></div>`;
    el.querySelector(".rb-bust")!.append(bramStill("alarm", 74));
    this.parent.append(el);
    this.el = el;
    if (motionOk() && typeof el.animate === "function") {
      el.querySelector<HTMLElement>(".rb-face")!.animate([{ transform: "scale(0.2) rotate(-25deg)", opacity: 0 }, { transform: "scale(1.15) rotate(4deg)", opacity: 1, offset: 0.7 }, { transform: "none", opacity: 1 }], { duration: RARE_BANNER.SlamMs, easing: "ease-out", fill: "backwards" });
      el.querySelector<HTMLElement>(".rb-title")!.animate([{ transform: "scale(2.4)", opacity: 0 }, { transform: "scale(0.94)", opacity: 1, offset: 0.6 }, { transform: "none", opacity: 1 }], { duration: 420, delay: RARE_BANNER.SlamMs - 120, easing: "ease-out", fill: "backwards" });
      el.querySelector<HTMLElement>(".rb-sub")!.animate([{ transform: "translateY(10px)", opacity: 0 }, { transform: "none", opacity: 1 }], { duration: 300, delay: RARE_BANNER.SlamMs + 200, fill: "backwards" });
      el.querySelector<HTMLElement>(".rb-bram")!.animate([{ transform: "translateX(-40px)", opacity: 0 }, { transform: "none", opacity: 1 }], { duration: 360, delay: RARE_BANNER.SlamMs + 420, easing: "cubic-bezier(.2,1.3,.4,1)", fill: "backwards" });
    }
    this.timer = window.setTimeout(() => this.leave(), RARE_BANNER.HoldMs + RARE_BANNER.SlamMs);
  }

  private leave(): void {
    const el = this.el;
    if (!el) return;
    if (!motionOk() || typeof el.animate !== "function") { this.hide(); return; }
    el.animate([{ opacity: 1 }, { opacity: 0, transform: "translate(-50%, -54%) scale(0.96)" }], { duration: 380, easing: "ease-in", fill: "forwards" }).onfinish = () => this.hide();
  }

  get shown(): boolean { return this.el !== null; }

  hide(): void { window.clearTimeout(this.timer); this.el?.remove(); this.el = null; }
}

/**
 * The boss bar turned rare: a gold plate with the rare's name and a RARE tag, a rainbow-gold fill that shimmers, and
 * a ring of the rare's colours around its portrait. Called as the rare rises (after the bar's own boss spawn).
 */
export function rareBar(bar: BossBar, def: RareDef): void {
  const root = bar.root;
  paint(root, def);
  root.classList.add("rare");
  const span = root.querySelector(".bb-name span");
  if (span) span.textContent = def.name;
  const tag = root.querySelector<HTMLElement>(".bb-tag");
  if (tag) { tag.textContent = "RARE"; }
  const img = root.querySelector<HTMLImageElement>(".bb-face img");
  const url = portraitUrl(def.id);
  if (img && url) { img.src = url; img.classList.remove("hidden"); }
}

/** Back to a plain boss bar (the next boss after a rare level, or a rebuilt level). */
export function plainBar(bar: BossBar): void {
  bar.root.classList.remove("rare");
  const tag = bar.root.querySelector<HTMLElement>(".bb-tag");
  if (tag) tag.textContent = "ENRAGED";
}

/**
 * The rare is slain and its chest is banked (M36 §3): a mid-level reveal over the map — the chest of its rarity rises
 * out of a burst of rays, its lid glows, the label names it ("LEGENDARY CHEST · BANKED") and Bram cheers — then the
 * card shrinks and flies into the HUD plate. The battle runs on underneath (the rare's stage is over, the next one
 * waits for the player): the reveal never pauses the sim. A tap sends it to the HUD at once.
 */
export class RareChestReveal {
  private el: HTMLDivElement | null = null;
  private timers: number[] = [];
  /** Seconds the card holds before it flies (a tap goes sooner). */
  static readonly HoldMs = 3000;
  static readonly FlyMs = 620;

  constructor(private readonly parent: HTMLElement, private readonly target: () => DOMRect | null) {}

  /**
   * `gear` / `gold`: what the chest actually banked (MetaFlow.rareGear, LevelCatalog.bankRareChest), shown as the
   * row of items under the chest so the player sees the prize, not just its rarity.
   */
  show(def: RareDef, rarity: number | null, B: BalanceData | null = null, gear: readonly GearItem[] = [], gold = 0): void {
    this.hide();
    const r = Math.max(0, Math.min(Rarity.Mythic, rarity ?? Rarity.Legendary)) as Rarity;
    const rid = RARITY_IDS[r];
    const el = document.createElement("div");
    el.className = `rare-chest ${rid}`;
    paint(el, def);
    const chest = portraitUrl(`chest_${rid}`);
    el.innerHTML = `<div class="rc-rays"></div><div class="rc-card"><div class="rc-chest">${chest ? `<img decoding="sync" src="${chest}" alt="">` : ""}<i class="rc-glow"></i></div>`
      + `<b class="rc-label disp">${RARITY_LABEL[r].toUpperCase()} CHEST</b><small class="rc-sub">from the ${def.name} · yours, win or lose</small>`
      + (B && (gear.length > 0 || gold > 0) ? `<div class="rc-items">${gear.map((it) => gearWellHtml(B, it, "sm")).join("")}${gold > 0 ? `<span class="rc-gold"><img decoding="sync" src="${resourceIconUrl("gold")}" alt="gold">${fmtCompact(gold)}</span>` : ""}</div>` : "")
      + `<div class="rc-bram"><div class="rc-bust"></div><div class="rc-say">${RARE_LINES.kill()}</div></div></div>`;
    el.querySelector(".rc-bust")!.append(bramStill("cheering", 64));
    el.addEventListener("click", () => this.fly());
    this.parent.append(el);
    this.el = el;
    if (motionOk() && typeof el.animate === "function") {
      el.querySelector<HTMLElement>(".rc-card")!.animate([{ transform: "translateY(60px) scale(0.4)", opacity: 0 }, { transform: "translateY(-6px) scale(1.06)", opacity: 1, offset: 0.65 }, { transform: "none", opacity: 1 }], { duration: 560, easing: "ease-out", fill: "backwards" });
      el.querySelector<HTMLElement>(".rc-chest img")?.animate([{ transform: "rotate(0)" }, { transform: "rotate(-6deg) scale(1.08)", offset: 0.25 }, { transform: "rotate(5deg) scale(1.08)", offset: 0.5 }, { transform: "rotate(-3deg)", offset: 0.75 }, { transform: "rotate(0)" }], { duration: 700, delay: 560 });
    }
    this.timers.push(window.setTimeout(() => this.fly(), RareChestReveal.HoldMs));
  }

  /** Off into the HUD plate (or simply away when the plate is not on screen). */
  private fly(): void {
    const el = this.el;
    if (!el || el.classList.contains("flying")) return;
    el.classList.add("flying");
    const card = el.querySelector<HTMLElement>(".rc-card")!;
    const to = this.target(), from = card.getBoundingClientRect();
    if (!motionOk() || typeof card.animate !== "function" || !to) { this.hide(); return; }
    const dx = to.left + to.width / 2 - (from.left + from.width / 2), dy = to.top + to.height / 2 - (from.top + from.height / 2);
    el.querySelector<HTMLElement>(".rc-rays")?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 260, fill: "forwards" });
    card.animate([{ transform: "none", opacity: 1 }, { transform: `translate(${dx * 0.4}px, ${dy * 0.4 - 30}px) scale(0.55)`, opacity: 1, offset: 0.45 }, { transform: `translate(${dx}px, ${dy}px) scale(0.12)`, opacity: 0.2 }], { duration: RareChestReveal.FlyMs, easing: "cubic-bezier(.5,0,.8,.6)", fill: "forwards" })
      .onfinish = () => this.hide();
  }

  get shown(): boolean { return this.el !== null; }

  hide(): void { for (const t of this.timers) window.clearTimeout(t); this.timers = []; this.el?.remove(); this.el = null; }
}

/**
 * The rare walked into the gate (M36 §3: the level is lost): its portrait cracks and greys under a dark red ribbon,
 * "THE GILDED STAG ESCAPED", and Bram's sorry line, held until the defeat screen takes over.
 */
export class RareEscape {
  private el: HTMLDivElement | null = null;
  private timer = 0;
  constructor(private readonly parent: HTMLElement) {}

  show(def: RareDef): void {
    this.hide();
    const el = document.createElement("div");
    el.className = "rare-escape";
    paint(el, def);
    const pic = portraitUrl(def.id);
    el.innerHTML = `<div class="re-face">${pic ? `<img decoding="sync" src="${pic}" alt="">` : ""}</div><div class="ribbon disp red re-title">THE ${def.name.toUpperCase()} ESCAPED</div>`
      + `<div class="plate re-sub">It broke through the gate — the battle is lost</div><div class="rb-bram re-bram"><div class="rb-bust"></div><div class="rb-say">${RARE_LINES.escape(def.name)}</div></div>`;
    el.querySelector(".rb-bust")!.append(bramStill("alarm", 64));
    this.parent.append(el);
    this.el = el;
    if (motionOk() && typeof el.animate === "function") {
      el.querySelector<HTMLElement>(".re-title")!.animate([{ transform: "scale(1.8)", opacity: 0 }, { transform: "scale(0.96)", opacity: 1, offset: 0.6 }, { transform: "none", opacity: 1 }], { duration: 480, easing: "ease-out", fill: "backwards" });
      el.querySelector<HTMLElement>(".re-face")!.animate([{ transform: "none" }, { transform: "translateX(-6px) rotate(-3deg)", offset: 0.2 }, { transform: "translateX(6px) rotate(3deg)", offset: 0.4 }, { transform: "translateX(-3px)", offset: 0.6 }, { transform: "none" }], { duration: 520 });
    }
    this.timer = window.setTimeout(() => this.hide(), 3200);
  }

  get shown(): boolean { return this.el !== null; }
  hide(): void { window.clearTimeout(this.timer); this.el?.remove(); this.el = null; }
}
